import { findChatById } from '@/lib/chatIndex';
import { useMemo } from 'react';
import { useChatStore, useCoworkerStore } from '@/store';
import { findTaskIdForConversation, taskIdFromChat } from '@/store/coworker';

const EMPTY = [];
const messageArtifactKeys = new WeakMap();

function messageArtifactsKey(messages = EMPTY) {
  if (messageArtifactKeys.has(messages)) return messageArtifactKeys.get(messages);
  const artifacts = new Map();
  for (const message of messages) {
    if (message.role === 'user') continue;
    for (const artifact of message.parameters?.coworkerArtifacts || EMPTY) {
      if (relativePath(artifact.path)) artifacts.set(artifact.path, artifact);
    }
  }
  const key = JSON.stringify([...artifacts.values()]);
  messageArtifactKeys.set(messages, key);
  return key;
}

function relativePath(value) {
  if (typeof value !== 'string') return null;
  let path;
  try { path = decodeURIComponent(value.trim()); } catch { return null; }
  if (/^[a-z][a-z\d+.-]*:|^[\/\\]|[?#\x00-\x1f\\]/i.test(path)) return null;
  path = path.replace(/^\.\//, '');
  if (path.split('/').some(part => !part || part === '.' || part === '..')) return null;
  return path || null;
}

export function findWorkArtifact(value, artifacts = EMPTY) {
  const path = relativePath(value);
  if (!path) return null;
  const exact = artifacts.filter(artifact => relativePath(artifact.path) === path);
  if (exact.length === 1) return exact[0];
  if (exact.length) return null;
  // Work exports preserve workspace subdirectories beneath artifacts/.
  // Resolve that exact staging prefix, without guessing another directory.
  const staged = artifacts.filter(artifact => relativePath(artifact.path) === `artifacts/${path}`);
  if (staged.length === 1) return staged[0];
  if (staged.length || path.includes('/')) return null;
  const matches = artifacts.filter(artifact => relativePath(artifact.path)?.split('/').pop() === path);
  return matches.length === 1 ? matches[0] : null;
}

// Only link exported artifacts belonging to this conversation's task. Relative
// Markdown filenames otherwise navigate to a nonexistent /c/<filename> page.
export default function useWorkArtifactReference(conversationId, enabled) {
  const linkedTaskId = useChatStore(s => enabled
    ? taskIdFromChat(findChatById(s.chats, conversationId)) : null);
  const taskId = useCoworkerStore(s => enabled
    ? linkedTaskId || findTaskIdForConversation(conversationId, { tasks: s.tasks, taskIds: s.taskIds }) : null);
  const artifacts = useCoworkerStore(s => s.tasks[taskId]?.artifacts || EMPTY);
  const messageKey = useChatStore(s => enabled
    ? messageArtifactsKey(findChatById(s.chats, conversationId)?.messages) : '[]');
  return useMemo(() => {
    const byPath = new Map([...JSON.parse(messageKey), ...artifacts].map(artifact => [artifact.path, artifact]));
    const exported = [...byPath.values()];
    return value => {
      if (!enabled || !taskId) return null;
      const artifact = findWorkArtifact(value, exported);
      return artifact ? { taskId, artifact } : null;
    };
  }, [enabled, taskId, artifacts, messageKey]);
}
